import { Link } from 'react-router-dom'
import { useContact } from '@/entities/contact/model'
import { contactLabel } from '@/entities/contact/labels'
import { SkeletonStack } from '@/shared/ui'
import type { ClientProfileFieldKey } from '@/shared/api/types'
import styles from './ClientBrief.module.css'

/** Что важно знать о клиенте с первого взгляда, по порядку. */
const BRIEF_FIELDS: ClientProfileFieldKey[] = [
  'position',
  'company',
  'city',
  'interest',
  'situation',
  'budget',
  'timeline',
  'objections',
  'agreements',
  'nextStep',
  'otherContacts',
]

/** Кто это: оценка, суть и главное из карточки клиента, со ссылкой на неё. */
export function ClientBrief({ contactId }: { contactId: number }) {
  const contact = useContact(contactId)
  const href = `/clients/${contactId}`

  if (contact.isPending) return <SkeletonStack rows={3} height={20} />
  if (!contact.data) {
    return (
      <Link className={styles.open} to={href}>
        Открыть карточку клиента →
      </Link>
    )
  }

  const { contact: info, profile, calls } = contact.data
  const name =
    profile.fields.find((field) => field.key === 'name')?.value.trim() ||
    info.displayName.trim() ||
    info.phoneFormatted
  const facts = BRIEF_FIELDS.map((key) => profile.fields.find((field) => field.key === key)).filter(
    (field) => field && field.value.trim() !== '',
  )
  const label = profile.label || info.label

  return (
    <div className={styles.brief}>
      <div className={styles.head}>
        <Link className={styles.name} to={href}>
          {name}
        </Link>
        <span className={styles.label} data-label={label || 'none'}>
          {contactLabel(label)}
        </span>
      </div>
      {name !== info.phoneFormatted && <div className={styles.phone}>{info.phoneFormatted}</div>}

      {profile.summary.trim() !== '' && <p className={styles.summary}>{profile.summary}</p>}

      {facts.length > 0 && (
        <dl className={styles.facts}>
          {facts.map((field) => (
            <div key={field!.key} className={styles.fact}>
              <dt>{field!.label}</dt>
              <dd>{field!.value}</dd>
            </div>
          ))}
        </dl>
      )}

      <div className={styles.meta}>
        Звонков: {calls.length}
        {info.note.trim() !== '' && <> · заметка: {info.note}</>}
      </div>

      <Link className={styles.open} to={href}>
        Открыть карточку клиента →
      </Link>
    </div>
  )
}
